import {AliffExporter, type DocumentService, type PcbPrimitiveStore, type PcbTracingGraph} from "@buildwithflux/core";
import type {AliffBoard, AliffExportTimeError, AutoLayoutJobConfiguration, NodeUid} from "@buildwithflux/models";
import {PcbNodeTypes} from "@buildwithflux/models";
import {Logger} from "@buildwithflux/shared";
import fileDownload from "js-file-download";

import {CurrentUserService} from "modules/auth";
import type {DocumentStore} from "modules/document/state/document";
import {getTypedBakedNode} from "modules/stores/pcb/utils";

import type {AutoLayoutBoardExporter} from "./AutoLayoutBoardExporter";
import {type AutoLayoutEventBus} from "./AutoLayoutEventBus";
import {AutoLayoutStoreService} from "./AutoLayoutStoreService";
import {DeepPcbAutoLayoutBaseService} from "./DeepPcbAutoLayoutBaseService";
import type {DeepPcbAutoLayoutEventMap} from "./DeepPcbAutoLayoutEventBus";

export interface DeepPcbBoardExportMetadata {
    objectIdToNodeUidMap: Record<string, string>;
    exportTimeErrors: AliffExportTimeError[];
}

/**
 * DeepPCB implementation of auto-layout board export.
 * Extracted from the monolithic DeepPcbAutoLayoutService.
 */
export class DeepPcbAutoLayoutBoardExporter
    extends DeepPcbAutoLayoutBaseService
    implements AutoLayoutBoardExporter<AliffBoard, DeepPcbBoardExportMetadata>
{
    constructor(
        eventBus: AutoLayoutEventBus<DeepPcbAutoLayoutEventMap>,
        logger: Logger,
        private readonly autoLayoutStoreService: AutoLayoutStoreService,
        private readonly pcbPrimitiveStore: PcbPrimitiveStore,
        private readonly documentStore: DocumentStore,
        private readonly documentService: DocumentService,
        private readonly currentUserService: CurrentUserService,
        private readonly pcbTracingGraph: PcbTracingGraph,
    ) {
        super("AutoLayoutBoardExporter", eventBus, logger);
    }

    public export(layoutNodeUid: NodeUid) {
        const currentJob = this.autoLayoutStoreService.getCurrentJob();
        if (!currentJob) {
            throw new Error("No active auto-layout job found");
        }

        const user = this.currentUserService.getCurrentUser();
        if (!user) {
            throw new Error("No current user found");
        }

        // Get document from Zustand store instead of Redux state
        const documentData = this.documentStore.getState().document;
        if (!documentData) {
            throw new Error("No document data found in Zustand store");
        }

        try {
            this.emit("board.export.started", {layoutNodeUid});

            const layoutNode = this.getLayoutNode(layoutNodeUid);

            const exporter = new AliffExporter(user, documentData, layoutNode, currentJob.config, {
                pcbPrimitiveStore: this.pcbPrimitiveStore,
                documentService: this.documentService,
                pcbTracingGraph: this.pcbTracingGraph,
                logger: this.logger,
            });

            const board = exporter.generateBoard();
            const metadata = exporter.getBoardExportMetadata();
            const boardString = JSON.stringify(board);

            this.logger.debug("Board generated for auto-layout", {
                layoutNodeUid,
            });

            this.emit("board.export.completed", {layoutNodeUid, metadata});

            return {
                board,
                boardString,
                metadata,
            };
        } catch (error) {
            this.emit("board.export.failed", {layoutNodeUid, error});
            throw error;
        }
    }

    public download(layoutNodeUid: NodeUid, config: AutoLayoutJobConfiguration, extension: string) {
        const layoutNode = this.getLayoutNode(layoutNodeUid);

        const user = this.currentUserService.getCurrentUser();
        if (!user) {
            throw new Error("No current user found");
        }

        // Get document from Zustand store instead of Redux state
        const documentData = this.documentStore.getState().document;
        if (!documentData) {
            throw new Error("No document data found in Zustand store");
        }

        const exporter = new AliffExporter(user, documentData, layoutNode, config, {
            pcbPrimitiveStore: this.pcbPrimitiveStore,
            documentService: this.documentService,
            pcbTracingGraph: this.pcbTracingGraph,
            logger: this.logger,
        });

        exporter.download(extension, fileDownload);
    }

    private getLayoutNode(layoutNodeUid: NodeUid) {
        const layoutNode = getTypedBakedNode(this.documentService, layoutNodeUid, PcbNodeTypes.layout);
        if (!layoutNode) {
            throw new Error(`Layout node with UID ${layoutNodeUid} not found in document`);
        }

        return layoutNode;
    }
}
